import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  Calendar,
  CheckCircle2,
  FileText,
  Hash,
  Layers,
  Receipt,
  Truck,
  Wallet,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatCurrency } from "@/utils/format";
import type { SiengeBillPreview, SiengeBillPreviewField } from "@/services/solicitacaoPagamento";

interface Props {
  open: boolean;
  onClose: () => void;
  preview: SiengeBillPreview | null;
  solicitacaoId?: string;
  canFinalize?: boolean;
  siengeBillId?: string | null;
  onFinalize?: () => Promise<string | void>;
}

const PREVIEW_FIELD_ORDER = [
  "Empresa (debtorId)",
  "Fornecedor (creditorId)",
  "Tipo documento",
  "Indexador (indexId)",
  "Data base correção",
  "Número documento",
  "Emissão",
  "Vencimento",
  "Parcelas",
  "Valor total",
] as const;

const FIELD_ICONS: Record<string, LucideIcon> = {
  "empresa (debtorid)": Building2,
  "fornecedor (creditorid)": Truck,
  "tipo documento": FileText,
  "indexador (indexid)": Hash,
  "data base correção": Calendar,
  "número documento": FileText,
  emissão: Calendar,
  vencimento: Calendar,
  parcelas: Layers,
  "valor total": Receipt,
};

function normalizeLabelKey(label: string): string {
  return label.trim().toLowerCase();
}

function parsePreviewMoney(value: string): number | null {
  const s = value.trim();
  if (!s) return null;
  if (s.includes(",")) {
    const normalized = s.replace(/\./g, "").replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function formatFieldValue(
  label: string,
  value: string,
  payloadAmount?: unknown,
): ReactNode {
  if (normalizeLabelKey(label) === "valor total") {
    const fromPayload =
      typeof payloadAmount === "number" && Number.isFinite(payloadAmount)
        ? payloadAmount
        : null;
    const n = fromPayload ?? parsePreviewMoney(value);
    if (n != null) {
      return <span className="font-bold text-slate-900">{formatCurrency(n)}</span>;
    }
    return <span className="font-bold text-slate-900">{value}</span>;
  }
  return value || "—";
}

function PreviewFieldRow({
  field,
  payloadAmount,
}: {
  field: SiengeBillPreviewField;
  payloadAmount?: unknown;
}) {
  const key = normalizeLabelKey(field.label);
  const Icon = FIELD_ICONS[key] ?? FileText;

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
        <Icon className="w-4 h-4 text-brand-600" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1 grid grid-cols-1 sm:grid-cols-[minmax(150px,220px)_1fr] gap-x-6 gap-y-0.5 items-baseline">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {field.label}
        </p>
        <div className="text-sm text-slate-900 break-words">
          {formatFieldValue(field.label, field.value, payloadAmount)}
        </div>
      </div>
    </div>
  );
}

export function SolicitacaoPagamentoBillPreviewModal({
  open,
  onClose,
  preview,
  canFinalize = false,
  siengeBillId,
  onFinalize,
}: Props) {
  const [finalizing, setFinalizing] = useState(false);
  const finalizingRef = useRef(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [finalizedId, setFinalizedId] = useState<string | null>(siengeBillId ?? null);

  useEffect(() => {
    if (open) {
      setFinalizedId(siengeBillId ?? null);
      setFinalizeError(null);
    }
  }, [open, siengeBillId]);

  const displayFields = useMemo(() => {
    if (!preview) return [];
    const byLabel = new Map(
      preview.fields.map((f) => [normalizeLabelKey(f.label), f] as const),
    );
    return PREVIEW_FIELD_ORDER.map((label) => byLabel.get(normalizeLabelKey(label))).filter(
      (f): f is SiengeBillPreviewField => f != null,
    );
  }, [preview]);

  const handleFinalize = async () => {
    if (!onFinalize || finalizingRef.current) return;
    finalizingRef.current = true;
    setFinalizing(true);
    setFinalizeError(null);
    try {
      const billId = await onFinalize();
      if (billId) setFinalizedId(billId);
      else setFinalizedId("ok");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : "Erro ao inserir titulo no Sienge.");
      setFinalizeError(msg);
    } finally {
      finalizingRef.current = false;
      setFinalizing(false);
    }
  };

  const successBillId =
    finalizedId && finalizedId !== "ok" ? finalizedId : siengeBillId ?? null;
  const showSuccess = Boolean(successBillId);

  const modalTitle = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
        <Wallet className="w-4 h-4 text-brand-600" strokeWidth={2} />
      </div>
      <span className="font-bold text-slate-900 truncate">Título a pagar — Sienge</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          {canFinalize && !showSuccess && (
            <Button onClick={() => void handleFinalize()} loading={finalizing}>
              Finalizar e inserir no Sienge
            </Button>
          )}
          <Button type="button" onClick={onClose} disabled={finalizing} variant={showSuccess ? "primary" : "secondary"}>
            Fechar
          </Button>
        </div>
      }
    >
      {preview ? (
        <div className="space-y-4">
          {showSuccess && (
            <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold">Título inserido no Sienge com sucesso.</p>
                {successBillId && (
                  <p className="mt-1 text-emerald-800">
                    ID do título: <span className="font-bold">{successBillId}</span>
                  </p>
                )}
                <p className="mt-1 text-emerald-800">
                  O anexo da solicitação e o relatório PDF foram enviados ao título.
                </p>
              </div>
            </div>
          )}

          {!showSuccess && canFinalize && (
            <p className="text-sm text-slate-600">
              Revise os dados abaixo. Ao finalizar, o título será criado no Sienge com o anexo da
              solicitação e o relatório PDF.
            </p>
          )}

          {preview.missingFields.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
              Campos pendentes: {preview.missingFields.join(", ")}
            </div>
          )}

          {finalizeError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
              {finalizeError}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white divide-y divide-slate-100">
            {displayFields.map((field) => (
              <PreviewFieldRow
                key={field.label}
                field={field}
                payloadAmount={preview.payload?.totalInvoiceAmount}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500">Nenhum preview disponível.</p>
      )}
    </Modal>
  );
}
